import { useTranslation } from 'react-i18next';

import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import useQuestions from '@/data/hook/question/useQuestions';
import useToggle from '@/data/hook/useToggle';
import { BreadcrumbItem } from '@/data/hook/useDynamicBreadcrumbItems';
import Activity from '@/models/Activity';
import { PlusIcon } from '@heroicons/react/24/outline';
import CreateQuestionModal from '../question/create-question-modal/CreateQuestionModal';
import QuestionCard from '../question/QuestionCard';
import FieldsetContainer from '../shared/FieldsetContainer';
import { Button } from '../ui/daisy';
import { Question, QuestionTypeEnum } from '@/models/Question';
import { useCallback, useState } from 'react';
import { DroppableList, DragDropPlaceholder } from '../ui/custom/dataInput/DragDrop';
import ConditionalRenderer from '../ui/custom/ConditionalRenderer';

type QuestionsListProps = {
  activity: Activity;
  cacheManager: ListCacheManager<Activity>;
  isDraggable?: boolean;
  breadcrumbItems?: BreadcrumbItem[];
  allowedTypes?: QuestionTypeEnum[];
  createQuestionBtnName?: string;
};

export default function QuestionsList({
  activity,
  cacheManager,
  breadcrumbItems,
  isDraggable,
  allowedTypes,
  createQuestionBtnName,
}: QuestionsListProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab',
  });

  const { isOpen, open, close } = useToggle(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question>();

  const { deleteQuestion } =
    useQuestions({
      activity,
      cacheManager,
    });

  const editQuestion = (question: Question) => {
    setSelectedQuestion(question);
    open();
  };

  const handleClose = useCallback(() => {
    setSelectedQuestion(undefined);
    close();
  }, [close]);

  const handleDelete = (identifier: string) => {
    deleteQuestion({ identifier, shouldShowAlert: true });
  };

  return (
    <div className="flex flex-col">
      <ConditionalRenderer
        condition={isDraggable}
        fallback={activity.questions?.map(question => (
          <QuestionCard
            key={question.identifier}
            question={question}
            onDelete={handleDelete}
            onEdit={editQuestion}
            className="mb-6"
          />
        ))}
      >
        <DroppableList
          groupId={String(activity.id)}
          type="QUESTION"
          className="relative min-h-12"
          placeholder={props => (
            <DragDropPlaceholder
              text={t('dndPlaceholder')}
              className="mb-6 cursor-pointer"
              {...props}
            />
          )}
        >
          {activity.questions?.map((question, index) => (
            <QuestionCard
              key={question.identifier}
              question={question}
              onDelete={handleDelete}
              onEdit={editQuestion}
              index={index}
              isDraggable
              id={question.identifier}
              className="mb-6"
            />
          ))}
        </DroppableList>
      </ConditionalRenderer>
      <FieldsetContainer>
        <Button color="primary" className="w-full" onClick={open}>
          <PlusIcon className="w-4 h-4" />
          {createQuestionBtnName ? t(createQuestionBtnName) : t('addQuestion')}
        </Button>
      </FieldsetContainer>

      <CreateQuestionModal
        breadcrumbItems={breadcrumbItems}
        activity={activity}
        cacheManager={cacheManager}
        visible={isOpen}
        initialQuestion={selectedQuestion}
        onClose={handleClose}
        allowedTypes={allowedTypes}
      />
    </div>
  );
}
